import type { NavTab } from '../ui/components/BottomNav';
import { getEnvironment } from './tokenStorage';

export type SolutionsSubView = 'list' | 'master_cross_checks' | 'construction_loan';

const SESSION_NAV_KEY = `courtyard_word_session_nav_${getEnvironment()}`;

export interface SessionNavigationSnapshot {
  viewMode: NavTab;
  solutionsSubView: SolutionsSubView;
  selectedProjectId: string;
  selectedSubProjectId: string;
  reviewKeepAlive: boolean;
}

const DEFAULT_SNAPSHOT: SessionNavigationSnapshot = {
  viewMode: 'clauses',
  solutionsSubView: 'list',
  selectedProjectId: '',
  selectedSubProjectId: '',
  reviewKeepAlive: false,
};

const VALID_VIEW_MODES = new Set<NavTab>([
  'review',
  'editor',
  'agent',
  'ask',
  'clauses',
  'proofread',
  'profile',
]);

const VALID_SOLUTIONS_SUB_VIEWS = new Set<SolutionsSubView>([
  'list',
  'master_cross_checks',
  'construction_loan',
]);

function parseSnapshot(raw: string): SessionNavigationSnapshot | null {
  try {
    const data = JSON.parse(raw) as Partial<SessionNavigationSnapshot>;
    if (!data || typeof data !== 'object') return null;

    const viewMode =
      typeof data.viewMode === 'string' && VALID_VIEW_MODES.has(data.viewMode as NavTab)
        ? (data.viewMode as NavTab)
        : DEFAULT_SNAPSHOT.viewMode;

    const solutionsSubView =
      typeof data.solutionsSubView === 'string' &&
      VALID_SOLUTIONS_SUB_VIEWS.has(data.solutionsSubView as SolutionsSubView)
        ? (data.solutionsSubView as SolutionsSubView)
        : DEFAULT_SNAPSHOT.solutionsSubView;

    return {
      viewMode: viewMode === 'profile' ? 'review' : viewMode,
      solutionsSubView,
      selectedProjectId: typeof data.selectedProjectId === 'string' ? data.selectedProjectId : '',
      selectedSubProjectId: typeof data.selectedSubProjectId === 'string' ? data.selectedSubProjectId : '',
      reviewKeepAlive: Boolean(data.reviewKeepAlive),
    };
  } catch {
    return null;
  }
}

export function loadSessionNavigation(): SessionNavigationSnapshot {
  if (typeof window === 'undefined') return DEFAULT_SNAPSHOT;
  const raw = localStorage.getItem(SESSION_NAV_KEY);
  if (!raw) return DEFAULT_SNAPSHOT;
  return parseSnapshot(raw) ?? DEFAULT_SNAPSHOT;
}

export function saveSessionNavigation(snapshot: SessionNavigationSnapshot): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(SESSION_NAV_KEY, JSON.stringify(snapshot));
  } catch {
    // Ignore quota / private-mode errors
  }
}
